import React, { useEffect, useState } from "react";

import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import {
  ComparePlan,
  FilterPlans,
  FlexComp,
  FormattedText,
  H5Tag,
  InsurancePlansComp,
  MoreBenefits,
  SpanTag,
  Tags,
  ToastCompare,
  TravelAssistanceModal,
} from "@tm/static";
import { usePlanType } from "@tm/ui";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";

import { trackEvent, triggerPageExit } from "@tm/insurance/analytics/xdm";
import { storePageTransitionStart } from "@tm/utility/analytics";
import { InsurancePlansStyle } from "./InsurancePlans.style";
import { insurancePlanType, IPlanData } from "./type";

const analytics = getAnalytics();

const InsurancePlans = ({
  data,
  title,
  quickFiltersTitle,
  quickFilters,
  setQuickFilters,
  tagData,
  filterData,
  compareToastData,
  comparePlan,
}: insurancePlanType) => {
  const device = useBreakpointV2();
  const [planSelected, setPlanSelected] = useState(data);
  const [count, setCount] = useState(0);

  const [quoteId1ForCompare, setQuoteId1ForCompare] = useState("");
  const [quoteId2ForCompare, setQuoteId2ForCompare] = useState("");
  const [productId1ForCompare, setProductId1ForCompare] = useState("");
  const [productId2ForCompare, setProductId2ForCompare] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [comparePlanText, setComparePlanText] = useState(
    "0/2 Products Selected"
  );
  const [isDisable, setIsDisable] = useState(true);
  const [showTravAssist, setshowTravAssist] = useState(-1);
  const [showBenefits, setShowBenefits] = useState(-1);
  const [showCompareNow, setShowCompareNow] = useState(false);

  const { type } = usePlanType();

  useEffect(() => {
    if (count === 1) {
      setComparePlanText("1/2 Products Selected");
    } else if (count > 1) {
      setComparePlanText("2/2 Products Selected");
    } else {
      setComparePlanText("0/2 Products Selected");
    }
  }, [count]);

  useEffect(() => {
    if (count === 2) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  }, [count]);

  useEffect(() => {
    if (quickFilters) {
      setPlanSelected(data);

      const selectedFiltersCardIds =
        quickFilters
          .filter((filter) => filter.isSelected)
          ?.reduce((acc: any, item: any) => {
            return [...acc, ...(item.cardIds || item.plans)];
          }, []) ?? [];

      const filteredCards = data?.filter((plan) => {
        return selectedFiltersCardIds.includes(plan.id);
      });

      filteredCards?.length && setPlanSelected(filteredCards);
    }
  }, [quickFilters]);

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const toggleBenefitsModal = (index: number) => {
    showBenefits === index ? setShowBenefits(-1) : setShowBenefits(index);
  };

  const handleTravAssist = (index: number) => {
    showTravAssist == index ? setshowTravAssist(-1) : setshowTravAssist(index);
  };

  const handleFilterChange = (id?: string | number) => {
    if (quickFilters) {
      const changedFilters = quickFilters.map((item: any) => {
        if (item.id == id) {
          analytics?.trackListingEvents(
            {
              actionType: "sum_insured_selected",
              payload: item.name,
            },
            "pageView",
            type
          );
          const productList = item?.quoteIds?.map((data: any) => {
            return {
              id: data.toString(),
            };
          });
          trackEvent({
            event_detail: {
              components: [{ product_list: productList }],
              event_type: "action",
              event_name: "component_cta-clicked",
              event_value: `sum_insured_selected_${item.name}`,
            },
          });

          return {
            ...item,
            isSelected: true,
          };
        } else {
          return {
            ...item,
            isSelected: false,
          };
        }
      });

      setQuickFilters(changedFilters);
    }
  };

  const handlePlanSelected = (productId: string) => {
    const updateCompareState = (item: any, id: string, quoteId: string) => {
      if (!item.selectedForCompare && count === 2) {
        return item;
      } else {
        item.selectedForCompare = !item.selectedForCompare;
      }

      if (item.selectedForCompare) {
        setCount(count + 1);
        if (quoteId1ForCompare == "") {
          setQuoteId1ForCompare(quoteId);
          setProductId1ForCompare(id);
        } else {
          setQuoteId2ForCompare(quoteId);
          setProductId2ForCompare(id);
        }
      } else {
        setCount(count - 1);
        if (quoteId == quoteId1ForCompare) {
          setQuoteId1ForCompare(quoteId2ForCompare);
          setProductId1ForCompare(productId2ForCompare);
        }
        setQuoteId2ForCompare("");
        setProductId2ForCompare("");
      }
    };

    const updatedPlanSelected = planSelected?.map((item) => {
      if (item?.cardData && productId === item.id) {
        updateCompareState(
          item.cardData,
          item.cardData.productId,
          item.cardData.quoteId
        );
      } else if (productId === item.productId) {
        updateCompareState(item, item.productId, item.quoteId);
      }
      return item;
    });

    setPlanSelected(updatedPlanSelected);
  };

  const handleSelectCompare = () => {
    if (!showCompareNow) {
      analytics?.trackListingEvents(
        {
          actionType: "comparenow_opened",
        },
        "pageView",
        type
      );
      trackEvent({
        event_detail: {
          components: [
            {
              product_list: [
                { id: productId1ForCompare },
                { id: productId2ForCompare },
              ],
            },
          ],
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "comparenow_opened",
        },
      });
    }
    setShowCompareNow(!showCompareNow);
  };

  const buyProduct = (quoteId: string, productId: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = Object.fromEntries(searchParams.entries());

    let params = {
      ...queryParams,
      countries: searchParams.get("countries"),
      startDate: searchParams.get("startDate"),
      endDate: searchParams.get("endDate"),
      travellers: searchParams.get("travellers"),
      isUpcomingTrip: searchParams.has("isUpcomingTrip")
        ? searchParams.get("isUpcomingTrip")
        : false,
      productId,
      quoteId,
    };
    triggerPageExit();
    storePageTransitionStart("listing");
    navigationActions.navigateToTravellerDetails(params);
  };

  // TODO: add logic to compare productId from comparisonIds

  const closeToast = () => {
    setPlanSelected(
      planSelected?.map((item: IPlanData) => {
        item.cardData
          ? (item.cardData.selectedForCompare = false)
          : (item.selectedForCompare = false);
        return item;
      })
    );

    data?.map((item: IPlanData) => {
      item.cardData
        ? (item.cardData.selectedForCompare = false)
        : (item.selectedForCompare = false);
    });

    setQuoteId1ForCompare("");
    setProductId1ForCompare("");

    return setCount(0);
  };

  return (
    <InsurancePlansStyle>
      <H5Tag margin="0 0 16px">
        {title && <FormattedText value={title} />}
      </H5Tag>

      {quickFiltersTitle && (
        <SpanTag
          color={theme.greyText}
          fontSize="sm"
          fontWeight="black"
          margin="0 0 8px"
        >
          {quickFiltersTitle}
        </SpanTag>
      )}

      {tagData && (
        <FlexComp
          alignItems={device == "lg" ? "center" : "flex-start"}
          justifyContent="space-between"
          margin={device == "lg" ? "0 0 40px" : "0 0 24px"}
          gap="10px"
          flexDirection={device == "lg" ? "row" : "column"}
        >
          {quickFilters && (
            <Tags handleClickTab={handleFilterChange} data={quickFilters} />
          )}
          {/* TODO: required later */}
          {/* <AnchorTag
                        handleClick={handleShowFilter}
                        fontSize="sm"
                        fontWeight="bold"
                        textTransform="uppercase"
                    >
                        {sortnFilterTxt && <FormattedText value={sortnFilterTxt} />}
                    </AnchorTag> */}
        </FlexComp>
      )}

      {planSelected?.map((item, index) => {
        const productType: "insurance" | "assistance" | "combo" =
          // @ts-ignore
          item?.cardData?.packName?.text?.value?.toLowerCase?.();
        const { cardType, cardData } = item || {};
        return (
          <React.Fragment key={item.id}>
            {cardType == "BANNER" ? (
              <FlexComp margin="0 0 24px">
                {cardData?.image?.url && (
                  <img
                    src={cardData?.image?.url}
                    width={
                      cardData?.image?.width ? cardData?.image?.width : "100%"
                    }
                    height={cardData?.image?.height}
                  />
                )}
              </FlexComp>
            ) : cardType == "PLAN" || !cardType ? (
              <InsurancePlansComp
                key={index}
                showCompareCheckbox={comparePlan ? true : false}
                data={cardData ? cardData : item}
                handleCompareSelect={() =>
                  handlePlanSelected(cardData ? item.id : item?.productId)
                }
                handleMoreBenefits={() => {
                  analytics?.trackListingEvents(
                    {
                      actionType: `View_details_${productType}`,
                    },
                    "pageView",
                    type
                  );
                  trackEvent({
                    event_detail: {
                      components: [
                        {
                          product_list: [{ id: item?.cardData?.productId }],
                        },
                      ],
                      event_type: "action",
                      event_name: "component_cta-clicked",
                      event_value: `View_benefits_clicked_${productType}`,
                    },
                  });
                  toggleBenefitsModal(index);
                }}
                handleTravAssist={() => {
                  handleTravAssist(index);
                  analytics?.trackListingEvents(
                    {
                      actionType: `View_details_${productType}`,
                    },
                    "pageView",
                    type
                  );
                }}
                handleBuyButton={(data) => {
                  analytics?.trackListingEvents(
                    {
                      actionType: `buy_now_clicked_outside_${productType}`,
                    },
                    "pageView",
                    type
                  );
                  trackEvent({
                    event_detail: {
                      components: [
                        {
                          product_list: [{ id: data?.productId }],
                        },
                      ],
                      event_type: "action",
                      event_name: "component_cta-clicked",
                      event_value: `buy_now_clicked_outside_${productType}`,
                    },
                  });
                  buyProduct(data?.quoteId, data?.productId);
                }}
              />
            ) : (
              <></>
            )}
            {showBenefits === index && (
              <MoreBenefits
                handleClose={() => toggleBenefitsModal(index)}
                moreBenfitsTitle={
                  item?.cardData
                    ? `${item?.cardData.totalBenefits} Benefits`
                    : `${item?.totalBenefits} Benefits`
                }
                data={item?.cardData ? item.cardData : item}
                handleBuyButton={(data) => {
                  analytics?.trackListingEvents(
                    {
                      actionType: `buy_now_clicked_details_${productType}`,
                    },
                    "pageView",
                    type
                  );
                  trackEvent({
                    event_detail: {
                      components: [
                        {
                          product_list: [{ id: data?.productId }],
                        },
                      ],
                      event_type: "action",
                      event_name: "component_cta-clicked",
                      event_value: `buy_now_clicked_details_${productType}`,
                    },
                  });
                  buyProduct(
                    item?.cardData ? item?.cardData.quoteId : data?.quoteId,
                    item?.cardData ? item?.cardData.productId : item?.productId
                  );
                }}
              />
            )}
            {showTravAssist == index && (
              <TravelAssistanceModal
                data={item.cardData ? item.cardData : item}
                handleTravAssist={() => handleTravAssist(index)}
                travelAssistTitle="Travel Assistance" // should come from CB
              />
            )}
          </React.Fragment>
        );
      })}
      {showFilter && (
        <FilterPlans
          tagData={tagData}
          btnText="Apply Filter"
          filterData={filterData}
          title={"Filter Plan"}
          filterPlanHandleClick={handleShowFilter}
        />
      )}
      {count > 0 && (
        <ToastCompare
          toastCloseIcon="https://tripmoneycmsimgak.mmtcdn.com/img/cross_grey_rounded_e24cbc15d7.svg"
          toastIcon="https://tripmoneycmsimgak.mmtcdn.com/img/toast_Icon_4389f5385c.svg"
          planSelectedTxt={comparePlanText}
          toastCtaTxt={compareToastData?.toastCtaTxt}
          handleClickCta={handleSelectCompare}
          handleClose={closeToast}
          isDisable={isDisable}
        />
      )}
      {showCompareNow && comparePlan && (
        <ComparePlan
          modalTitle={compareToastData?.modalTitle}
          data={comparePlan}
          handleClose={handleSelectCompare}
          handleBuyButton={(quoteId, productId) => {
            analytics?.trackListingEvents(
              {
                actionType: "buy_from_compare",
              },
              "pageView",
              type
            );
            trackEvent({
              event_detail: {
                components: [{ product_list: [{ id: productId }] }],
                event_type: "action",
                event_name: "component_cta-clicked",
                event_value: "buy_from_compare",
              },
            });
            buyProduct(quoteId, productId);
          }}
          productId1ForCompare={productId1ForCompare}
          quoteId1ForCompare={quoteId1ForCompare}
          productId2ForCompare={productId2ForCompare}
          quoteId2ForCompare={quoteId2ForCompare}
        />
      )}
    </InsurancePlansStyle>
  );
};

export default InsurancePlans;
